import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, LoadingState, Tabs, TabPanel, apiRequest, getBootstrapData } from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import type { BreadcrumbItem } from '@tpct/admin-ui';
import BreadcrumbBar from '@tpct/mission-core/components/BreadcrumbBar';
import type { CompanyPageData } from './types/company-page';
import DashboardTab from './tabs/DashboardTab';
import InfoTab from './tabs/InfoTab';
import ThemesTab from './tabs/ThemesTab';
import CompanyFilesTab from './components/files/CompanyFilesTab';
import type { PortalSearchSyncFn } from '@/portal/src/utils/portalUrlState';
import { parsePortalSearchParams, pickAllowedParam } from '@/portal/src/utils/portalUrlState';

const LEGACY_TAB_IDS = ['dashboard', 'info', 'themes'] as const;
const LIVE_TAB_IDS = [...LEGACY_TAB_IDS, 'files'] as const;
type CompanyTabId = (typeof LIVE_TAB_IDS)[number];

/** File store live (portal bootstrap `fileStoreLive`): the "Fichiers" tab replaces the legacy FEC/DSN import. */
const readFileStoreLive = (bootstrapKey: string): boolean => {
  try {
    const bootstrap: object = getBootstrapData(bootstrapKey);
    return 'fileStoreLive' in bootstrap && bootstrap.fileStoreLive === true;
  } catch {
    return false;
  }
};

type CompanyPageAppProps = {
  companyId: number;
  bootstrapKey: string;
  navigateTo?: (path: string) => void;
  search?: string;
  patchPortalSearch?: PortalSearchSyncFn;
};

const CompanyPageApp = ({
  companyId,
  bootstrapKey,
  navigateTo,
  search,
  patchPortalSearch,
}: CompanyPageAppProps) => {
  const { data, isLoading, isError } = useQuery<CompanyPageData>({
    queryKey: ['company', companyId],
    queryFn: () =>
      apiRequest<CompanyPageData>(`company/${companyId}`, {}, bootstrapKey),
    enabled: companyId > 0,
  });

  const fileStoreLive = useMemo(() => readFileStoreLive(bootstrapKey), [bootstrapKey]);
  const params = useMemo(() => parsePortalSearchParams(search ?? ''), [search]);
  const activeTab = pickAllowedParam<CompanyTabId>(
    params.get('tab'),
    fileStoreLive ? LIVE_TAB_IDS : LEGACY_TAB_IDS,
    'dashboard',
  );

  const portalTabs = typeof patchPortalSearch === 'function';

  if (companyId <= 0) {
    return <EmptyState title={__('Entreprise introuvable')} description={__("L'identifiant fourni est invalide.")} />;
  }

  if (isLoading) {
    return <LoadingState columns={[__('Champ'), __('Valeur')]} className="company-page-loading" />;
  }

  if (isError || !data) {
    return (
      <EmptyState
        title={__('Erreur de chargement')}
        description={__("Impossible de charger les données de l'entreprise.")}
      />
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    ...(navigateTo
      ? [{ label: __('Mes dossiers'), onClick: () => navigateTo('/companies') }]
      : []),
    { label: data.name },
  ];

  const onTabChange = (tabId: string): void => {
    if (!patchPortalSearch) {
      return;
    }
    const next = tabId as CompanyTabId;
    patchPortalSearch({ tab: next === 'dashboard' ? null : next }, 'push');
  };

  const panels = [
    <TabPanel key="dashboard" id="dashboard" label={__('Tableau de bord')}>
      <DashboardTab companyId={companyId} bootstrapKey={bootstrapKey} />
    </TabPanel>,
    <TabPanel key="info" id="info" label={__('Informations')}>
      <InfoTab
        data={data}
        queryKey={['company', companyId]}
        bootstrapKey={bootstrapKey}
        fileStoreLive={fileStoreLive}
      />
    </TabPanel>,
    <TabPanel key="themes" id="themes" label={__('Thèmes et logo')}>
      <ThemesTab
        companyId={companyId}
        data={data}
        queryKey={['company', companyId]}
        bootstrapKey={bootstrapKey}
      />
    </TabPanel>,
    ...(fileStoreLive
      ? [
        <TabPanel key="files" id="files" label={__('Fichiers')}>
          <CompanyFilesTab companyId={companyId} bootstrapKey={bootstrapKey} />
        </TabPanel>,
      ]
      : []),
  ];

  return (
    <div className="company-page">
      <BreadcrumbBar items={breadcrumbItems} />
      {portalTabs ? (
        <Tabs activeTab={activeTab} onActiveTabChange={onTabChange} defaultActiveTab="dashboard">
          {panels}
        </Tabs>
      ) : (
        <Tabs defaultActiveTab="dashboard">{panels}</Tabs>
      )}
    </div>
  );
};

export default CompanyPageApp;
